import { useLocation } from "react-router-dom";
import {
  ConditionalRenderer,
  Header,
  useLanguageTranslator,
} from "../../../components";
import {
  CountryAction,
  ThemesAction,
  GeographicalAction,
  PolicyCategoriesAction,
  PracticeCategoriesAction,
} from "../parameters/features";
import { Stack, Typography } from "@mui/material";
import { IndicatorValuesAction } from "../data/features";
import { IndicatorAction } from "../parameters/features/indicatorsAction";
import { UsersAction } from "../accounts/features";
import { ProfileAction } from "../accounts/features/profileAction";
import { IndicatorsCategoriesAction } from "../parameters/features/indicatorsCategoriesAction";
import { InstitutionsAction } from "../parameters/features/institutionsAction";
import { RolesAction } from "../parameters/features/roleAction";

const moduleKeys = {
  country: "country",
  themes: "themes",
  geographicalArea: "geographical-area",
  policyCategories: "policy-categories",
  practiceCategories: "practice-categories",
  indicatorsCategories: "indicators-categories",
  indicatorsValues: "indicators-values",
  indicators: "indicators",
  users: "users",
  profiles: "profiles",
  institution: "institution",
  role: "role",
};

function UpdateFeatures() {
  const location = useLocation();
  const moduleName = location?.pathname?.split("/")?.[2];
  const isUpdate = location?.pathname?.includes("update");
  const { translator } = useLanguageTranslator();

  function returnTitle(moduleKeys: any, moduleName: any) {
    if (moduleKeys?.country === moduleName) {
      return translator("Country");
    }
    if (moduleKeys?.themes === moduleName) {
      return `${translator("Theme")}s`;
    }
    if (moduleKeys?.geographicalArea === moduleName) {
      return translator("Geographical Area");
    }
    if (moduleKeys?.policyCategories === moduleName) {
      return translator("Policy Categories");
    }

    if (moduleKeys?.indicatorsCategories === moduleName) {
      return translator("Indicators Categories");
    }

    if (moduleKeys?.indicatorsValues === moduleName) {
      return translator("Indicators Values");
    }

    if (moduleKeys?.indicators === moduleName) {
      return translator("Indicators");
    }

    if (moduleKeys?.users === moduleName) {
      return translator("Users");
    }

    if (moduleKeys?.profiles === moduleName) {
      return translator("generic.Profile");
    }

    if (moduleKeys?.institution === moduleName) {
      return translator(`Institutions`)
    }

    if (moduleKeys?.role === moduleName) {
      return translator(`Role`)
    }

    return "Practice Categories";
  }

  const title = returnTitle(moduleKeys, moduleName);

  return (
    <>
      <Header
        title={title}
        subtitle=""
      />
      <ConditionalRenderer condition={moduleKeys?.country === moduleName}>
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} {translator("Country")}
          </Typography>
        </Stack>
        <CountryAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={moduleKeys?.themes === moduleName}>
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} {`${translator("Theme")}`}
          </Typography>
        </Stack>
        <ThemesAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={moduleKeys?.geographicalArea === moduleName}
      >
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")}{" "}
            {`${translator("Geographical Area")}`}
          </Typography>
        </Stack>
        <GeographicalAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={moduleKeys?.policyCategories === moduleName}
      >
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} {translator("Policy Category")}{" "}
          </Typography>
        </Stack>
        <PolicyCategoriesAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={moduleKeys?.practiceCategories === moduleName}
      >
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} {translator("Practice Category")}{" "}
          </Typography>
        </Stack>
        <PracticeCategoriesAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={moduleKeys?.indicatorsCategories === moduleName}
      >
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} New{" "}
            {translator(`Indicators Categories`)}
          </Typography>
        </Stack>
        <IndicatorsCategoriesAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={moduleKeys?.indicatorsValues === moduleName}
      >
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} New{" "}
            {`${translator("Indicators Values")}`}
          </Typography>
        </Stack>
        <IndicatorValuesAction isUpdate={isUpdate} />
      </ConditionalRenderer>
       <ConditionalRenderer
        condition={moduleKeys?.institution === moduleName}
      >
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} New{" "}
            {translator(`Institutions`)}
          </Typography>
        </Stack>
        <InstitutionsAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={moduleKeys?.role === moduleName}
      >
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} New{" "}
            {`Role`}
          </Typography>
        </Stack>
        <RolesAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={moduleKeys?.indicators === moduleName}>
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} New Indicator
          </Typography>
        </Stack>
        <IndicatorAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={moduleKeys?.users === moduleName}>
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} User Infos
          </Typography>
        </Stack>
        <UsersAction isUpdate={isUpdate} />
      </ConditionalRenderer>
      <ConditionalRenderer condition={moduleKeys?.profiles === moduleName}>
        <Stack direction={"row"} sx={{ background: "#ffffff" }}>
          <Typography variant="subtitle1" mx={4} mt={4} noWrap>
            {translator("generic.Update")} Profile Infos
          </Typography>
        </Stack>
        <ProfileAction isUpdate={isUpdate} />
      </ConditionalRenderer>
    </>
  );
}

export { UpdateFeatures };
